import React, { useContext, useMemo } from 'react';
import Constants from 'expo-constants';
import PropTypes from 'prop-types';
import RenderHtml from 'react-native-render-html';
import SpanRenderer from './renderers/span';
import ImageRenderer from './renderers/classic/image';
import LinkRenderer from './renderers/link';
import LayoutContext from '../../../contexts/Layout';

const SystemFonts = [...Constants.systemFonts, 'BarlowMedium', 'BarlowRegular', 'Plantin', 'GothamBook', 'GothamMedium', 'FrankRuhlLibre'];

const CustomRenderers = {
    a: LinkRenderer,
    img: ImageRenderer,
    span: SpanRenderer
};

export const StyledWebView = ({ customStyle, html, renderersProps, textProps, width }) => {
    const { layout } = useContext(LayoutContext);
    const pLineHeight = Math.floor(layout.styleGuide.p1.fontSize + 1.25);

    const memoedStyleGuideTagStyles = useMemo(() => {
        return {
            a: {
                color: layout.styleGuide.link?.color,
                fontFamily: layout.styleGuide.link?.fontFamily,
                fontSize: layout.styleGuide.link?.fontSize,
                textDecorationColor: layout.styleGuide.link?.color,
                textDectorationLine: 'underline',
                textDecorationStyle: 'solid'
            },
            blockquote: {
                borderLeftColor: '#a61a2b',
                borderLeftWidth: layout.device.os.equals('web') ? '3pt' : 3,
                color: '#000',
                fontFamily: 'FrankRuhlLibre',
                fontWeight: 'bold',
                fontSize: layout.device.os.equals('web') ? '18pt' : 18,
                lineHeight: layout.device.os.equals('web') ? '22pt' : 22,
                paddingHorizontal: 10
            },
            h1: {
                color: layout.styleGuide.h1.color,
                fontFamily: layout.styleGuide.h1.fontFamily,
                fontSize: layout.styleGuide.h1.fontSize
            },
            h2: {
                color: layout.styleGuide.h2.color,
                fontFamily: layout.styleGuide.h2.fontFamily,
                fontSize: layout.styleGuide.h2.fontSize
            },
            h3: {
                color: layout.styleGuide.h3.color,
                fontFamily: layout.styleGuide.h3.fontFamily,
                fontSize: layout.styleGuide.h3.fontSize
            },
            h4: {
                color: layout.styleGuide.h4.color,
                fontFamily: layout.styleGuide.h4.fontFamily,
                fontSize: layout.styleGuide.h4.fontSize
            },
            h5: {
                color: layout.styleGuide.h5.color,
                fontFamily: layout.styleGuide.h5.fontFamily,
                fontSize: layout.styleGuide.h5.fontSize
            },
            h6: {
                color: layout.styleGuide.h6.color,
                fontFamily: layout.styleGuide.h6.fontFamily,
                fontSize: layout.styleGuide.h6.fontSize
            },
            li: {
                color: '#7f868b',
                fontFamily: 'FrankRuhlLibre',
                fontSize: layout.styleGuide.p1.fontSize,
                lineHeight: layout.device.os.equals('web') ? `${pLineHeight}pt` : pLineHeight,
                listStyle: 'disc'
            },
            p: {
                color: layout.styleGuide.p1.color,
                fontFamily: layout.styleGuide.p1.fontFamily,
                fontSize: layout.styleGuide.p1.fontSize,
                lineHeight: layout.device.os.equals('web') ? `${pLineHeight}pt` : pLineHeight,
                margin: 0
            },
            ul: {
                alignItems: 'center',
                display: 'flex',
                margin: 0,
                marginLeft: 5
            }
        };
    }, [layout, pLineHeight]);

    const memoedClassesStyles = useMemo(() => {
        return {
            btn: {
                borderRadius: 2,
                fontFamily: 'GothamBook'
            },
            'text-white': {
                color: '#fff',
                textDectorationLine: 'none'
            }
        };
    }, []);

    const memoedRendererProps = useMemo(() => {
        return {
            ...renderersProps,
            ul: {
                markerBoxStyle: {
                    justifyContent: 'center',
                    marginVertical: 5,
                    paddingRight: 5
                },
                markerTextStyle: {
                    color: '#7f868b'
                }
            }
        };
    }, [renderersProps]);

    return (
        <RenderHtml
            classesStyles={memoedClassesStyles}
            // contentWidth={width}
            defaultTextProps={{ ...textProps, selectable: true }}
            emSize={16}
            enableCSSInlineProcessing={true}
            enableExperimentalMarginCollapsing={true}
            enableExperimentalBRCollapsing={false}
            enableExperimentalGhostLinesPrevention={false}
            ignoredDomTags={['body', 'html']}
            ignoredStyles={['accessibilityLabel ', 'textShadow', 'textDectorationLine']}
            renderers={CustomRenderers}
            renderersProps={memoedRendererProps}
            source={{
                html: html,
                baseUrl: Constants.manifest2?.extra?.baseUrl
            }}
            systemFonts={SystemFonts}
            tagsStyles={{ ...memoedStyleGuideTagStyles, ...customStyle }}
        />
    );
};

StyledWebView.propTypes = {
    customStyle: PropTypes.object,
    html: PropTypes.string.isRequired,
    textProps: PropTypes.object,
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};
